import React from "react";
import { CheckCircle2, Info } from "lucide-react";
import { SubscribeButton } from "@/components/SubscribeButton";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { TIERS } from "@/data/content";

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <Seo
        title="Plans & Pricing — Hussar Marine Concierge"
        description="Monthly dockside oversight plans for Southwest Florida vessel owners: Anchor Sentinel $299, Premier Vigilance $499, Admiralty Suite $799."
      />
      <PageHero
        eyebrow="Monthly Plans"
        title="Choose your level of vigilance"
        subtitle="Transparent monthly plans scaled to how you use your vessel. Subscriptions are billed monthly and payments are processed securely by Stripe."
      />

      <section className="py-20 lg:py-28 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {TIERS.map((t, i) => (
              <Reveal key={t.id} delay={i * 110}>
                <div
                  className={`card-lift relative h-full rounded-2xl p-8 flex flex-col ${
                    t.popular
                      ? "bg-navy-900 border-2 border-gold-500 shadow-2xl md:-mt-4"
                      : "bg-ivory dark:bg-navy-800 border border-gold-500/20"
                  }`}
                  data-testid={t.testid}
                >
                  {t.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-500 text-navy-900 text-xs font-bold px-4 py-1 rounded-full eyebrow">
                      {t.badge}
                    </span>
                  )}
                  <h3 className={`font-serif text-2xl font-semibold ${t.popular ? "text-white" : "text-navy-900 dark:text-white"}`}>
                    {t.name}
                  </h3>
                  <p className={`mt-2 text-sm ${t.popular ? "text-slate-300" : "text-slate-600 dark:text-slate-300"}`}>{t.description}</p>
                  <div className="mt-6 flex items-end gap-1">
                    <span className={`text-5xl font-bold ${t.popular ? "text-gold-400" : "text-navy-900 dark:text-white"}`}>${t.price}</span>
                    <span className={`mb-1.5 text-sm ${t.popular ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}`}>/ month</span>
                  </div>
                  <div className="gold-rule my-6 opacity-60" />
                  <ul className="space-y-3.5 flex-1">
                    {t.features.map((f) => (
                      <li key={f} className={`flex items-start gap-2.5 text-sm ${t.popular ? "text-slate-200" : "text-slate-600 dark:text-slate-300"}`}>
                        <CheckCircle2 className="h-4 w-4 text-gold-500 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <SubscribeButton
                    planId={t.id}
                    className={`mt-8 inline-flex disabled:opacity-60 items-center justify-center gap-2 font-semibold px-6 py-3.5 rounded-full transition-all ${
                      t.popular
                        ? "bg-gold-500 hover:bg-gold-400 text-navy-900"
                        : "border border-navy-900/20 dark:border-white/20 text-navy-900 dark:text-white hover:border-gold-500"
                    }`}
                  />
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-12 max-w-3xl mx-auto flex items-start gap-3 rounded-xl border border-gold-500/30 bg-gold-100/60 dark:bg-navy-800 p-5">
              <Info className="h-5 w-5 text-gold-600 mt-0.5 shrink-0" />
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Vessels over 60 ft, multi-vessel fleets, and custom schedules are quoted
                individually. All plans cover visual dockside oversight and coordination
                only — repairs and specialized work are performed by licensed third-party
                providers you approve. <strong>Recurring monthly payments are processed securely by Stripe; HMC does not store your card details. Sign in to your owner account to subscribe.</strong>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
